import dayjs from 'dayjs';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { Country } from './country.js';
import { Language } from './language.js';
import { TimeZoneRegion } from './time-zone-region.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { EventProfile, EventProfileStatus } from './event-profile.js';
import { LocalizedString } from './localized-string.js';
import { DateSpan } from './date-span.js';
import { PhotoReference } from './file-reference/photo-reference.js';
import { ProfilePhotosComponent } from '../../components/misc/profile-photos-component.js';
import { LocalizedNameComponent } from '../../components/point-of-interest/localized-name-component.js';
import { DateSpanComponent } from '../../components/misc/date-span-component.js';
import { cloneData, isSameData } from '../dto/data-model-utils.js';
import { ErrorLike } from '../core/error-like.js';
import { Result } from '../../utility/result.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';

export class EventMembership extends DataModel {
  static readonly TYPE_ID: string = '272e0a2604b4462f96f4e3416f70157a';

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly country: Country,
    public readonly language: Language,
    public readonly timezone: TimeZoneRegion,
    public readonly businessRef: BusinessModuleRef,
    public readonly webRoute: string | null,
    public readonly placeId: string | null,
    public readonly status: EventProfileStatus,
    public readonly names: LocalizedString,
    public readonly dateSpan: DateSpan,
    public readonly cover: PhotoReference,
  ) {
    super();
  }

  get $typeId(): string {
    return EventMembership.TYPE_ID;
  }

  static create(args: {
    uniqueId: string,
    creatorId: string,
    creationDate: dayjs.Dayjs,
    country: Country,
    language: Language,
    timezone: TimeZoneRegion,
    businessRef: BusinessModuleRef,
    webRoute: string | null,
    placeId: string | null,
    status: EventProfileStatus,
    names: LocalizedString,
    dateSpan: DateSpan,
    cover: PhotoReference,
  }): EventMembership {
    return new EventMembership(
        args.uniqueId,
        args.creatorId,
        args.creationDate,
        args.country,
        args.language,
        args.timezone,
        args.businessRef,
        args.webRoute,
        args.placeId,
        args.status,
        args.names,
        args.dateSpan,
        args.cover,
    );
  }

  static fromEventProfile(eventProfile: EventProfile): Result<EventMembership, ErrorLike> {
    const dateSpanComponent = eventProfile.findComponentByTypeId<DateSpanComponent>(
        DateSpanComponent.TYPE_ID,
    );

    if(dateSpanComponent == null) {
      return Result.error('EventProfile does not have a DateSpanComponent');
    }

    const namesComponent = eventProfile.findComponentByTypeId<LocalizedNameComponent>(
        LocalizedNameComponent.TYPE_ID,
    );

    if(namesComponent == null) {
      return Result.error('EventProfile does not have a LocalizedNameComponent');
    }

    const profilePhotosComponent = eventProfile.findComponentByTypeId<ProfilePhotosComponent>(
        ProfilePhotosComponent.TYPE_ID,
    );

    if(profilePhotosComponent == null) {
      return Result.error('EventProfile does not have a ProfilePhotosComponent');
    }

    const profilePhoto = profilePhotosComponent.portfolio.photos.at(0);
    if(profilePhoto == null) {
      return Result.error('EventProfile does not have a cover photo');
    }

    return Result.ok(this.create({
      uniqueId: eventProfile.uniqueId,
      creatorId: eventProfile.creatorId,
      creationDate: eventProfile.creationDate,
      country: eventProfile.country,
      businessRef: cloneData(eventProfile.businessRef),
      cover: profilePhoto,
      dateSpan: cloneData(dateSpanComponent.dateSpan),
      language: eventProfile.language,
      names: cloneData(namesComponent.names),
      placeId: eventProfile.placeId,
      status: eventProfile.status,
      timezone: eventProfile.timezone,
      webRoute: eventProfile.webRoute,
    }));
  }

  static fromMap(context: SerializationContext, map: SerializedData): EventMembership {
    return this.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      creatorId: MapUtils.getString(map, 'creatorId'),
      creationDate: context.decodeDate(MapUtils.getNumber(map, 'creationDate')),
      country: toEnumFromString(MapUtils.getString(map, 'country'), Country),
      language: toEnumFromString(MapUtils.getString(map, 'language'), Language),
      timezone: toEnumFromString(MapUtils.getString(map, 'timezone'), TimeZoneRegion),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      webRoute: MapUtils.getStringOrNull(map, 'webRoute'),
      placeId: MapUtils.getStringOrNull(map, 'placeId'),
      status: toEnumFromString(MapUtils.getString(map, 'status'), EventProfileStatus),
      names: context.fromData(MapUtils.getRecord(map, 'names')),
      dateSpan: context.fromData(MapUtils.getRecord(map, 'dateSpan')),
      cover: context.fromData(MapUtils.getRecord(map, 'cover')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId,
      creatorId: this.creatorId,
      creationDate: context.encodeDate(this.creationDate),
      country: fromEnumAsString(this.country),
      language: fromEnumAsString(this.language),
      timezone: fromEnumAsString(this.timezone),
      businessRef: this.businessRef.encode(),
      webRoute: this.webRoute,
      placeId: this.placeId,
      status: fromEnumAsString(this.status),
      names: context.toData(this.names),
      dateSpan: context.toData(this.dateSpan),
      cover: context.toData(this.cover),
    };
  }

  clone(): this {
    return EventMembership.create({
      uniqueId: this.uniqueId,
      creatorId: this.creatorId,
      creationDate: TimeZone.cloneDate(this.creationDate),
      country: this.country,
      language: this.language,
      timezone: this.timezone,
      businessRef: cloneData(this.businessRef),
      webRoute: this.webRoute,
      placeId: this.placeId,
      status: this.status,
      names: cloneData(this.names),
      dateSpan: cloneData(this.dateSpan),
      cover: cloneData(this.cover),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.creatorId === this.creatorId &&
      other.creationDate === this.creationDate &&
      other.country === this.country &&
      other.language === this.language &&
      other.timezone === this.timezone &&
      isSameData(other.businessRef, this.businessRef) &&
      other.webRoute === this.webRoute &&
      other.placeId === this.placeId &&
      other.status === this.status &&
      isSameData(other.names, this.names) &&
      isSameData(other.dateSpan, this.dateSpan) &&
      isSameData(other.cover, this.cover);
  }
}

export class EventMembershipBuilder extends DataModelBuilder<EventMembership> {
  build(context: SerializationContext, map: SerializedData): EventMembership {
    return EventMembership.fromMap(context, map);
  }
}
